import { z } from 'zod';

import logger from 'logger';
import { RootState, AppDispatch } from 'store/configureStore';
import createAppSlice from 'store/createAppSlice';
import {
  getLeadPhotoTagInfo,
  isPhotoTagConfirmed,
  isPhotoTagNeeedsReview,
  isPhotoTagRejected,
  isPhotoTagReviewed,
  isPhotoTagTmpNeeedsReview,
} from 'lib/photoTag';
import { selectPhoto } from 'slices/leadSlice';
import { InventoryItemFormSubmitHandlerValues } from 'components/inventory/InventoryItemForm';
import {
  addInventoryToPhoto,
  savePhotoTagTmpStatuses,
} from 'actions/leadActions';
import { photoTagStatus } from '@backend/src/lib/lead/constants';

export const InventoryItemType = z.enum([
  'add',
  'photoUpload',
  'photoItemReview',
  'photoItemCombine',
  'photoItemEdit',
  'photoItemDelete',
  'photoFinish',
  'photoConfirmRemoved',
  'photoAddItemPrompt',
  'photoAddItem',
  'manualAddItem',
]);

export type InventoryItemType = z.infer<typeof InventoryItemType>;

// const item = z.object({
//   _id: z.string(),
//   itemType: z.number(),
// });

// type Item = z.infer<typeof item>;

// const itemGroup = z.object({
//   itemType: z.string(),
//   items: z.array(item),
// });

// type ItemGroup = z.infer<typeof itemGroup>;

export const InventoryItem = z.object({
  photoId: z.string().optional(),
  currentView: InventoryItemType.optional(),
  currentId: z.string().optional(),
  underReview: z.array(z.string()).optional(),
});

// export const none = z.object({
//   type: z.literal(InventoryItemType.Enum.none),
// });

// export const add = z.object({
//   type: z.literal(InventoryItemType.Enum.add),
// });

// export const photoUpload = z.object({
//   type: z.literal(InventoryItemType.Enum.photoUpload),
// });

// export const photoUpload = z.object({
//   type: z.literal(InventoryItemType.Enum.photoUpload),
//   upload: Upload,
// });

const photoItem = InventoryItem.required({
  photoId: true,
  currentId: true,
});

const photoItemReview = photoItem.extend({
  currentView: z.literal(InventoryItemType.Enum.photoItemReview),
});

const photoItemCombine = photoItem.extend({
  currentView: z.literal(InventoryItemType.Enum.photoItemCombine),
});

// export const photoItemEdit = z.object({
//   type: z.literal(InventoryItemType.Enum.photoItemEdit),
//   photoId: z.string(),
//   itemId: z.string(),
// });

// export const photoItemDelete = z.object({
//   type: z.literal(InventoryItemType.Enum.photoItemDelete),
//   photoId: z.string(),
//   itemId: z.string(),
// });

// export const photoAddItem = z.object({
//   type: z.literal(InventoryItemType.Enum.photoAddItem),
//   photoId: z.string(),
// });

// export const photoAddItemPrompt = z.object({
//   type: z.literal(InventoryItemType.Enum.photoAddItemPrompt),
//   photoId: z.string(),
// });

// export const photoFinish = z.object({
//   type: z.literal(InventoryItemType.Enum.photoFinish),
//   photoId: z.string(),
// });

// export const photoConfirmRemoved = z.object({
//   type: z.literal(InventoryItemType.Enum.photoConfirmRemoved),
//   photoId: z.string(),
// });

// export const manualAddItem = z.object({
//   type: z.literal(InventoryItemType.Enum.manualAddItem),
// });

// export const InventoryItem = z.discriminatedUnion('type', [
//   // none,
//   add,
//   // photoAdd,
//   photoUpload,
//   photoItemReview,
//   // photoItemEdit,
//   // photoItemDelete,
//   photoFinish,
//   photoConfirmRemoved,
//   photoItemCombine,
//   photoAddItem,
//   photoAddItemPrompt,
//   manualAddItem,
// ]);

export type InventoryItem = z.infer<typeof InventoryItem>;

// export const SoftDeletedTags = z.record(z.string(), z.string());

// export type SoftDeletedTags = z.infer<typeof SoftDeletedTags>;

export const SPACE_ADD = 'add';

export const InventorySliceState = z.object({
  spaceId: z.string().optional(),
  inventoryItem: InventoryItem.optional(),
  // softDeletedTags: SoftDeletedTags.optional(),
});

export type InventorySliceState = z.infer<typeof InventorySliceState>;

function assertPhotoItemReviewOrCombine(
  inventoryItem?: InventoryItem,
): asserts inventoryItem is
  | z.infer<typeof photoItemReview>
  | z.infer<typeof photoItemCombine> {
  if (
    !(
      inventoryItem &&
      (inventoryItem.currentView === InventoryItemType.Enum.photoItemReview ||
        inventoryItem.currentView === InventoryItemType.Enum.photoItemCombine)
    )
  ) {
    throw new Error('not photoItemReview or photoItemCombine');
  }
}

const initialState: InventorySliceState = {
  spaceId: undefined,
};

export const inventorySlice = createAppSlice({
  name: 'inventory',
  initialState,
  reducers: (create) => ({
    clearInventorySpaceId: create.reducer((state) => {
      state.spaceId = undefined;
    }),
    setInventorySpaceId: create.reducer<string>((state, action) => {
      state.spaceId = action.payload;
    }),
    clearInventoryItem: create.reducer((state) => {
      state.inventoryItem = undefined;
      // state.softDeletedTags = undefined;
    }),
    setInventoryItem: create.reducer<InventoryItem>((state, action) => {
      state.inventoryItem = action.payload;
    }),
    setInventoryItemTagId: create.reducer<string>((state, action) => {
      assertPhotoItemReviewOrCombine(state.inventoryItem);
      state.inventoryItem.currentId = action.payload;
    }),
    // addSoftDeletedTag: create.reducer<string>((state, action) => {
    //   state.softDeletedTags = {
    //     ...state.softDeletedTags,
    //     [action.payload]: action.payload,
    //   };
    // }),
    // removeSoftDeletedTag: create.reducer<string>((state, action) => {
    //   state.softDeletedTags = omit(state.softDeletedTags, action.payload);
    // }),
    startInventoryItemReview: create.asyncThunk(
      async (
        {
          photoId,
          // unconfirmedOnly = true,
        }: { photoId: string },
        thunkApi,
      ) => {
        const dispatch = thunkApi.dispatch as AppDispatch;

        await dispatch(
          savePhotoTagTmpStatuses({
            photoId,
          }),
        );

        const state = thunkApi.getState() as RootState;

        const photo = selectPhoto(state, photoId);

        if (!photo) {
          throw new Error(`photo ${photoId} not found`);
        }

        const approvedItems = photo.tags.filter(isPhotoTagReviewed);

        if (approvedItems.length > 0) {
          await dispatch(
            addInventoryToPhoto({
              spaceId: photo.spaceId,
              photoId,
              items: approvedItems.map((item) => {
                return {
                  tagId: item._id,
                  itemType: item.itemType || 0,
                  quantity: 1,
                };
              }),
            }),
          );
        }

        const firstTag = photo.tags.find(isPhotoTagNeeedsReview);

        logger.info('startInventoryItemReview', { firstTag, approvedItems });

        if (!firstTag) {
          dispatch(
            inventorySlice.actions.finishInventoryItemReviewItem({ photoId }),
          );
        } else {
          dispatch(
            inventorySlice.actions.setInventoryItem({
              currentView: 'photoItemReview',
              photoId,
              currentId: firstTag?._id || '',
            }),
          );
        }
      },
    ),
    nextInventoryItemReviewItemOrCombine: create.asyncThunk(
      async (args: { formerIndex: number } | void, thunkApi) => {
        const { formerIndex } = args || {};
        const state = thunkApi.getState() as RootState;
        const dispatch = thunkApi.dispatch as AppDispatch;

        logger.info('nextInventoryItemReviewItemOrCombine', { formerIndex });

        assertPhotoItemReviewOrCombine(state.inventory.inventoryItem);

        const { inventoryItem } = state.inventory;

        const { photoId } = inventoryItem;

        const photo = selectPhoto(state, photoId);

        if (!photo) {
          throw new Error(`photo ${photoId} not found`);
        }

        const {
          index,
          tag: currentTag,
          hasNext,
          nextTag,
        } = getLeadPhotoTagInfo({
          photo,
          tagId: inventoryItem.currentId,
          unconfirmedOnly: true,
        });

        const softDeletedTagIds = photo.tags
          .filter(isPhotoTagRejected)
          .map((tag) => tag._id);

        logger.trace('nextInventoryItemReviewItemOrCombine tagInfo', {
          index,
          tag: currentTag,
          hasNext,
          nextTag,
        });

        if (!hasNext) {
          dispatch(inventorySlice.actions.finishInventoryItemReviewItem());
          return;
        }

        // const tags = inventoryItem.unconfirmedOnly
        //   ? photo.tags.filter((tag) => !isPhotoTagConfirmed(tag))
        //   : photo.tags;

        const tags = photo.tags.filter(isPhotoTagTmpNeeedsReview);

        logger.trace('nextInventoryItemReviewItemOrCombine tags', {
          tags,
        });

        const numUpcomingTagsWithSameName = tags
          .slice(index >= 0 ? index + 1 : formerIndex)
          .filter(
            (tag) =>
              tag.Name === currentTag.Name &&
              !softDeletedTagIds.includes(tag._id),
          ).length;

        logger.trace(
          'nextInventoryItemReviewItemOrCombine numUpcomingTagsWithSameName',
          {
            numUpcomingTagsWithSameName,
          },
        );

        // There are 2 or more upcoming tags with the same name, so we prompt to be combined with this item.
        const shouldCombine = numUpcomingTagsWithSameName >= 2;

        logger.trace('nextInventoryItemReviewItemOrCombine shouldCombine', {
          shouldCombine,
        });

        dispatch(
          inventorySlice.actions.setInventoryItem({
            currentView: shouldCombine
              ? InventoryItemType.Enum.photoItemCombine
              : InventoryItemType.Enum.photoItemReview,
            photoId,
            currentId: shouldCombine ? inventoryItem.currentId : nextTag?._id,
            // unconfirmedOnly: inventoryItem.unconfirmedOnly,
          }),
        );
      },
    ),
    nextInventoryItemReviewItem: create.asyncThunk(
      async (_: void, thunkApi) => {
        const state = thunkApi.getState() as RootState;
        const dispatch = thunkApi.dispatch as AppDispatch;

        assertPhotoItemReviewOrCombine(state.inventory.inventoryItem);

        const { inventoryItem } = state.inventory;

        const { photoId } = inventoryItem;

        const photo = selectPhoto(state, photoId);

        if (!photo) {
          throw new Error(`photo ${photoId} not found`);
        }

        const photoTagInfo = getLeadPhotoTagInfo({
          photo,
          tagId: inventoryItem.currentId,
          unconfirmedOnly: true,
        });

        const { nextTag } = photoTagInfo;

        logger.info('nextInventoryItemReviewItem', {
          inventoryItem,
          photoTagInfo,
        });

        dispatch(
          inventorySlice.actions.setInventoryItem({
            currentView: 'photoItemReview',
            photoId,
            currentId: nextTag?._id,
            // unconfirmedOnly: inventoryItem.unconfirmedOnly,
          }),
        );
      },
    ),
    nextUnconfirmedInventoryItemReviewItem: create.asyncThunk(
      async (_: void, thunkApi) => {
        const state = thunkApi.getState() as RootState;
        const dispatch = thunkApi.dispatch as AppDispatch;

        assertPhotoItemReviewOrCombine(state.inventory.inventoryItem);

        const { inventoryItem } = state.inventory;

        const { photoId } = inventoryItem;

        const photo = selectPhoto(
          // We get the state again since adding inventory updates the photo tags.
          state,
          photoId,
        );

        if (!photo) {
          throw new Error(`photo ${photoId} not found`);
        }

        const { index } = getLeadPhotoTagInfo({
          photo,
          tagId: inventoryItem.currentId,
          unconfirmedOnly: true,
        });

        const firstUpcomingUnconfirmedPhotoTag = photo.tags
          .slice(index + 1)
          .find((tag) => !isPhotoTagConfirmed(tag));

        logger.info('nextUnconfirmedInventoryItemReviewItem', {
          index,
          firstUpcomingUnconfirmedPhotoTag,
        });

        if (firstUpcomingUnconfirmedPhotoTag) {
          dispatch(
            inventorySlice.actions.setInventoryItem({
              currentView: 'photoItemReview',
              photoId,
              currentId: firstUpcomingUnconfirmedPhotoTag?._id,
              // unconfirmedOnly: inventoryItem.unconfirmedOnly,
            }),
          );
        } else {
          dispatch(inventorySlice.actions.finishInventoryItemReviewItem());
        }
      },
    ),
    previousInventoryItemReviewItem: create.asyncThunk(
      async (_: void, thunkApi) => {
        const state = thunkApi.getState() as RootState;
        const dispatch = thunkApi.dispatch as AppDispatch;

        assertPhotoItemReviewOrCombine(state.inventory.inventoryItem);

        const { inventoryItem } = state.inventory;

        const { photoId } = inventoryItem;

        const photo = selectPhoto(state, photoId);

        if (!photo) {
          throw new Error(`photo ${photoId} not found`);
        }

        const { previousTag } = getLeadPhotoTagInfo({
          photo,
          tagId: inventoryItem.currentId,
          unconfirmedOnly: true,
        });

        logger.info('previousInventoryItemReviewItem', { previousTag });

        dispatch(
          inventorySlice.actions.setInventoryItem({
            currentView: 'photoItemReview',
            photoId,
            currentId: previousTag?._id,
          }),
        );
      },
    ),
    submitInventoryItemReviewItem: create.asyncThunk(
      async ({ itemType }: InventoryItemFormSubmitHandlerValues, thunkApi) => {
        const state = thunkApi.getState() as RootState;
        const dispatch = thunkApi.dispatch as AppDispatch;

        assertPhotoItemReviewOrCombine(state.inventory.inventoryItem);

        const { inventoryItem, spaceId } = state.inventory;

        const { photoId } = inventoryItem;

        const photo = selectPhoto(state, photoId);

        if (!photo) {
          throw new Error(`photo ${photoId} not found`);
        }

        const { index, tag } = getLeadPhotoTagInfo({
          photo,
          tagId: inventoryItem.currentId,
          unconfirmedOnly: true,
        });

        logger.info('submitInventoryItemReviewItem', {
          index,
          itemType,
          tag,
        });

        if (tag.status !== photoTagStatus.Rejected) {
          await dispatch(
            addInventoryToPhoto({
              spaceId,
              photoId,
              items: [
                {
                  tagId: inventoryItem.currentId,
                  itemType,
                  quantity: 1,
                },
              ],
            }),
          );

          dispatch(
            inventorySlice.actions.nextInventoryItemReviewItemOrCombine({
              formerIndex: index,
            }),
          );
        } else {
          dispatch(inventorySlice.actions.nextInventoryItemReviewItem());
        }
      },
    ),
    combineInventoryItemReviewItem: create.asyncThunk(
      async (
        { tagIds, itemType }: { tagIds: string[]; itemType: number },
        thunkApi,
      ) => {
        const state = thunkApi.getState() as RootState;
        const dispatch = thunkApi.dispatch as AppDispatch;

        assertPhotoItemReviewOrCombine(state.inventory.inventoryItem);

        const { inventoryItem, spaceId } = state.inventory;

        const { photoId } = inventoryItem;

        logger.info('combineInventoryItemReviewItem', {
          tagIds,
          itemType,
        });

        await dispatch(
          addInventoryToPhoto({
            spaceId,
            photoId,
            items: tagIds.map((tagId) => ({
              tagId,
              itemType,
              quantity: 1,
            })),
          }),
        );

        dispatch(
          inventorySlice.actions.nextUnconfirmedInventoryItemReviewItem(),
        );
      },
    ),
    finishInventoryItemReviewItem: create.asyncThunk(
      async (args: { photoId?: string } | undefined, thunkApi) => {
        const state = thunkApi.getState() as RootState;
        const dispatch = thunkApi.dispatch as AppDispatch;

        const { inventoryItem } = state.inventory;

        const photoId = args?.photoId || inventoryItem?.photoId;

        logger.info('finishInventoryItemReviewItem', {
          photoId,
        });

        dispatch(
          inventorySlice.actions.setInventoryItem({
            currentView: 'photoFinish',
            photoId,
          }),
        );
      },
    ),
  }),
});

export const {
  setInventorySpaceId,
  clearInventorySpaceId,
  clearInventoryItem,
  setInventoryItem,
  startInventoryItemReview,
  nextInventoryItemReviewItem,
  previousInventoryItemReviewItem,
  submitInventoryItemReviewItem,
  combineInventoryItemReviewItem,
  finishInventoryItemReviewItem,
  setInventoryItemTagId,
  // addSoftDeletedTag,
  // removeSoftDeletedTag,
} = inventorySlice.actions;

export default inventorySlice;

export const selectInventorySpaceId = (
  state: RootState,
): InventorySliceState['spaceId'] => state.inventory?.spaceId;

export const selectInventoryItem = (
  state: RootState,
): InventoryItem | undefined => state.inventory?.inventoryItem;

// export const selectInventorySoftDeletedTags = (
//   state: RootState,
// ): SoftDeletedTags | undefined => state.inventory?.softDeletedTags;
